import { ReactNode, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, Link } from "@mui/material";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatRelative } from "./time";
import { InquiryDetail, InquiryFacts, InquiryPayment } from "./types";
import { INK, OUTLINED } from "./styles";

type Props = {
  payment: InquiryPayment | null;
  facts: InquiryFacts;
  factsAt: string | null;
  currency: string;
  timeline: InquiryDetail['timeline'];
  onRefresh: () => void;
  refreshing: boolean;
  loading: boolean;
  onFindOrder: () => void;
};

const ORDER_TYPE_LABELS: Record<InquiryFacts['orderType'], string> = { renewal: "Renewal", initial: "New sale", unknown: "Unknown" };

const ACTIVITY_PREVIEW = 5;

const DOT_COLORS: Record<InquiryDetail['timeline'][number]['kind'], string> = {
  seller_message: '#465fff',
  buyer_message: '#98a2b3',
  opened: '#f79009',
  offer: '#f79009',
  status: '#f79009',
  due: '#f79009',
  failed: '#d92d20',
  proof_requested: '#175cd3',
  evidence_sent: '#067647',
};

type FactRow = [string, ReactNode, string?];

const day = (value: string | null) => (value ? dayjs(value).format('MMM DD, YYYY') : '—');

function FactRows({ rows }: { rows: FactRow[] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', gap: '6px 10px', color: INK.body }}>
      {rows.map(([label, value, title]) => (
        <Box key={label} sx={{ display: 'contents' }}>
          <Box title={title} sx={{ color: INK.muted, minWidth: 0, whiteSpace: 'nowrap' }}>{label}</Box>
          <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{value}</Box>
        </Box>
      ))}
    </Box>
  );
}

function paymentRows(payment: InquiryPayment): FactRow[] {
  return [
    ["Paid", day(payment.paidAt)],
    ["Amount", payment.amount !== null && payment.currency ? `${formatCurrency(payment.amount, payment.currency)} ${payment.currency}` : '—'],
    ["Item", payment.items.length ? payment.items.map((item) => `${item.name} × ${item.quantity}`).join(', ') : '—'],
    ["Buyer", payment.buyerEmail ?? '—'],
    ["PayPal ID", payment.transactionId
      ? (payment.url ? <Link href={payment.url} target="_blank" rel="noopener noreferrer" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>{payment.transactionId}</Link> : payment.transactionId)
      : '—'],
    ["Seller protection", payment.protectionEligible === null ? '—' : payment.protectionEligible ? 'Eligible' : 'Not eligible', "PayPal Seller Protection on this payment"],
  ];
}

export default function InquiryFactsPanel({ payment, facts, factsAt, currency, timeline, onRefresh, refreshing, loading, onFindOrder }: Props) {
  const [showAll, setShowAll] = useState(false);
  const cancelled = !!facts.subscriptionCancelledAt;
  const activeRenewal = !cancelled && facts.orderType === 'renewal';
  const rows: FactRow[] = [
    ["Placed", day(facts.placedAt)],
    ["Type", ORDER_TYPE_LABELS[facts.orderType], "New sale or subscription renewal"],
    ["Renewed", day(facts.renewedAt)],
    ["Subscription", (
      <Box component="span" sx={{ color: activeRenewal ? INK.danger : undefined }}>
        {cancelled ? `Cancelled ${day(facts.subscriptionCancelledAt)}` : activeRenewal ? "Active" : '—'}
      </Box>
    )],
    ["Shipped", facts.shippedAt ? [day(facts.shippedAt), facts.carrier].filter(Boolean).join(', ') : '—'],
    ["Tracking", facts.trackingNumber
      ? (facts.trackingUrl ? <Link href={facts.trackingUrl} target="_blank" rel="noopener noreferrer">{facts.trackingNumber}</Link> : facts.trackingNumber)
      : '—'],
    ["Refunds", facts.refundedTotal === null ? '—' : facts.refundedTotal ? formatCurrency(facts.refundedTotal, currency) : "None"],
  ];
  const reload = (
    <IconButton size="small" aria-label="Reload order" title="Re-read the order from the store" onClick={onRefresh} disabled={refreshing}>
      {refreshing ? <CircularProgress size={14} /> : <RefreshOutlined fontSize="small" />}
    </IconButton>
  );
  return (
    <Box
      component="aside"
      sx={{
        flex: { xl: '0 0 clamp(260px, 28%, 330px)' },
        minHeight: 0,
        overflowY: 'auto',
        bgcolor: '#fff',
        borderLeft: { xl: `1px solid ${INK.border}` },
        borderTop: { xs: `1px solid ${INK.border}`, xl: 'none' },
        p: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        fontSize: 13,
      }}
    >
      {payment && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', borderBottom: `1px solid ${INK.border}`, pb: '12px' }}>
          <Box sx={{ fontWeight: 600 }}>Payment</Box>
          <FactRows rows={paymentRows(payment)} />
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: INK.muted }}>
          <CircularProgress size={14} />
          <span>Reading the order…</span>
        </Box>
      ) : !facts.orderName && !facts.error ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
          <Box sx={{ fontWeight: 600 }}>No order linked</Box>
          <Box sx={{ fontSize: 12, color: INK.muted }}>Find the order to fill the templates and the order details.</Box>
          <Button variant="outlined" size="small" sx={OUTLINED} onClick={onFindOrder}>Find the order</Button>
        </Box>
      ) : facts.error ? (
        <>
          <Alert severity="warning">{facts.error.replace(/^Order facts unavailable: /, "Order unavailable: ")}</Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {reload}
            <Button variant="outlined" size="small" sx={OUTLINED} onClick={onFindOrder}>Find the order</Button>
          </Box>
        </>
      ) : (
        <>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
              <span>Order {facts.orderName}</span>
              {reload}
            </Box>
            {factsAt && <Box sx={{ fontSize: 11, color: INK.muted }}>Order read {formatRelative(factsAt)}</Box>}
          </Box>
          <FactRows rows={rows} />
        </>
      )}
      {timeline.length > 0 && (
        <Box sx={{ borderTop: `1px solid ${INK.border}`, pt: '10px' }}>
          <Box sx={{ fontWeight: 600, color: INK.body, mb: '4px' }}>Activity</Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <Box sx={{ position: 'absolute', left: '3px', top: '10px', bottom: '10px', width: '2px', bgcolor: '#eaecf0' }} />
            {(showAll ? timeline : timeline.slice(0, ACTIVITY_PREVIEW)).map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: '12px', py: '4px', position: 'relative' }}>
                <Box data-testid="activity-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DOT_COLORS[item.kind], flex: '0 0 8px', mt: '5px' }} />
                <Box>
                  <Box sx={{ fontSize: 12, color: '#344054' }}>{item.label}</Box>
                  <Box sx={{ fontSize: 11, color: '#667085' }}>{dayjs(item.at).format('MMM DD, HH:mm')}</Box>
                </Box>
              </Box>
            ))}
          </Box>
          {timeline.length > ACTIVITY_PREVIEW && (
            <Link component="button" type="button" underline="hover" sx={{ fontSize: 11, mt: '4px' }} onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show less' : `Show all ${timeline.length}`}
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
}
